/**
 * Rate limiting utilities for various operations
 * Used to prevent flooding of errors, API calls, and other operations
 */

// Type definitions
interface RateLimitOptions {
  windowMs?: number;
  maxAttempts?: number;
  keyGenerator?: ((key: string) => string) | null;
  skipSuccessfulRequests?: boolean;
  skipFailedRequests?: boolean;
}

interface RateLimitStatus {
  attempts: number;
  remaining: number;
  resetTime: number;
  isLimited: boolean;
  windowStart?: number;
  firstAttempt?: number;
  lastAttempt?: number | null;
}

interface RateLimiter {
  check: (key?: string) => boolean;
  getStatus: (key?: string) => RateLimitStatus;
  reset: (key?: string) => void;
  resetAll: () => void;
  getAll: () => Record<string, Omit<RateLimitStatus, 'windowStart' | 'firstAttempt' | 'lastAttempt'>>;
  updateConfig: (newConfig: Partial<RateLimitOptions>) => void;
  getConfig: () => RateLimitOptions;
  destroy: () => void;
}

interface TokenBucketOptions {
  capacity?: number;
  refillRate?: number;
  refillAmount?: number;
}

interface TokenBucketStatus {
  tokens: number;
  capacity: number;
  refillRate: number;
  lastRefill: number;
}

interface TokenBucket {
  consume: (count?: number) => boolean;
  available: () => number;
  getStatus: () => TokenBucketStatus;
  reset: () => void;
}

interface SlidingWindowOptions {
  windowMs?: number;
  maxRequests?: number;
}

interface SlidingWindowStatus {
  requests: number;
  remaining: number;
  windowStart: number;
  windowEnd: number;
  resetTime: number;
}

interface SlidingWindow {
  isAllowed: (key?: string) => boolean;
  getStatus: (key?: string) => SlidingWindowStatus;
  clear: (key?: string) => void;
  clearAll: () => void;
}

/**
 * Create a rate limiter instance
 * @param {RateLimitOptions} options - Rate limiter configuration
 * @returns {RateLimiter} Rate limiter instance
 */
export const rateLimit = (options: RateLimitOptions = {}): RateLimiter => {
  const config = {
    windowMs: 60000, // 1 minute window
    maxAttempts: 100, // Max attempts per window
    keyGenerator: null, // Custom key generator function
    skipSuccessfulRequests: false,
    skipFailedRequests: false,
    ...options,
  };
  
  // Storage for rate limit data
  const store = new Map();
  
  // Cleanup old entries periodically
  const cleanup = () => {
    const now = Date.now();
    for (const [key, data] of Array.from(store.entries())) {
      if (now - data.windowStart > config.windowMs) {
        store.delete(key);
      }
    }
  };
  
  // Set up periodic cleanup
  const cleanupInterval = setInterval(cleanup, config.windowMs);
  
  return {
    /**
     * Check if an operation is allowed
     * @param {string} key - Unique key for the operation
     * @returns {boolean} True if operation is allowed
     */
    check: (key: string = 'default'): boolean => {
      const now = Date.now();
      const identifier = config.keyGenerator ? config.keyGenerator(key) : key;
      
      // Get or create rate limit data for this key
      let data = store.get(identifier);
      
      if (!data || (now - data.windowStart) > config.windowMs) {
        // New window or first attempt
        data = {
          attempts: 0,
          windowStart: now,
          firstAttempt: now,
          lastAttempt: null,
        };
        store.set(identifier, data);
      }
      
      // Check if limit exceeded
      if (data.attempts >= config.maxAttempts) {
        return false;
      }
      
      // Record this attempt
      data.attempts++;
      data.lastAttempt = now;
      
      return true;
    },
    
    /**
     * Get current rate limit status for a key
     * @param {string} key - Unique key for the operation
     * @returns {Object} Rate limit status
     */
    getStatus: (key: string = 'default'): RateLimitStatus => {
      const identifier = config.keyGenerator ? config.keyGenerator(key) : key;
      const data = store.get(identifier);
      const now = Date.now();
      
      if (!data || (now - data.windowStart) > config.windowMs) {
        return {
          attempts: 0,
          remaining: config.maxAttempts,
          resetTime: now + config.windowMs,
          isLimited: false,
        };
      }
      
      return {
        attempts: data.attempts,
        remaining: Math.max(0, config.maxAttempts - data.attempts),
        resetTime: data.windowStart + config.windowMs,
        isLimited: data.attempts >= config.maxAttempts,
        windowStart: data.windowStart,
        firstAttempt: data.firstAttempt,
        lastAttempt: data.lastAttempt,
      };
    },
    
    /**
     * Reset rate limit for a specific key
     * @param {string} key - Key to reset
     */
    reset: (key: string = 'default'): void => {
      const identifier = config.keyGenerator ? config.keyGenerator(key) : key;
      store.delete(identifier);
    },
    
    /**
     * Reset all rate limits
     */
    resetAll: (): void => {
      store.clear();
    },
    
    /**
     * Get all current rate limit data
     * @returns {Object} All rate limit data
     */
    getAll: (): Record<string, Omit<RateLimitStatus, 'windowStart' | 'firstAttempt' | 'lastAttempt'>> => {
      const result: Record<string, Omit<RateLimitStatus, 'windowStart' | 'firstAttempt' | 'lastAttempt'>> = {};
      const now = Date.now();
      
      for (const [key, data] of Array.from(store.entries())) {
        if ((now - data.windowStart) <= config.windowMs) {
          result[key] = {
            attempts: data.attempts,
            remaining: Math.max(0, config.maxAttempts - data.attempts),
            resetTime: data.windowStart + config.windowMs,
            isLimited: data.attempts >= config.maxAttempts,
          };
        }
      }
      
      return result;
    },
    
    /**
     * Update rate limiter configuration
     * @param {Object} newConfig - New configuration options
     */
    updateConfig: (newConfig: Partial<RateLimitOptions>): void => {
      Object.assign(config, newConfig);
    },
    
    /**
     * Get current configuration
     * @returns {Object} Current configuration
     */
    getConfig: (): RateLimitOptions => ({ ...config }),
    
    /**
     * Destroy rate limiter and clean up resources
     */
    destroy: (): void => {
      clearInterval(cleanupInterval);
      store.clear();
    },
  };
};

/**
 * Decorator function to add rate limiting to any function
 * @param {Function} fn - Function to rate limit
 * @param {RateLimitOptions} options - Rate limit options
 * @returns {Function} Rate limited function
 */
export const withRateLimit = <T extends (...args: any[]) => any>(
  fn: T,
  options: RateLimitOptions = {}
): T => {
  const limiter = rateLimit(options);

  return function(this: any, ...args: any[]) {
    const key = options.keyGenerator ? options.keyGenerator(args.join(':')) : 'default';

    if (!limiter.check(key)) {
      const status = limiter.getStatus(key);
      const error = new Error(`Rate limit exceeded. Try again after ${new Date(status.resetTime).toISOString()}`);
      (error as any).rateLimitStatus = status;
      throw error;
    }

    return fn.apply(this, args);
  } as T;
};

/**
 * Async version of rate limited function decorator
 * @param {Function} fn - Async function to rate limit
 * @param {RateLimitOptions} options - Rate limit options
 * @returns {Function} Rate limited async function
 */
export const withRateLimitAsync = <T extends (...args: any[]) => Promise<any>>(
  fn: T,
  options: RateLimitOptions = {}
): T => {
  const limiter = rateLimit(options);

  return async function(this: any, ...args: any[]) {
    const key = options.keyGenerator ? options.keyGenerator(args.join(':')) : 'default';

    if (!limiter.check(key)) {
      const status = limiter.getStatus(key);
      const error = new Error(`Rate limit exceeded. Try again after ${new Date(status.resetTime).toISOString()}`);
      (error as any).rateLimitStatus = status;
      throw error;
    }

    try {
      const result = await fn.apply(this, args);
      return result;
    } catch (error) {
      // Optionally don't count failed requests against the limit
      if (options.skipFailedRequests) {
        limiter.reset(key);
      }
      throw error;
    }
  } as T;
};

/**
 * Token bucket rate limiter for smooth rate limiting
 * @param {TokenBucketOptions} options - Token bucket configuration
 * @returns {TokenBucket} Token bucket rate limiter
 */
export const tokenBucket = (options: TokenBucketOptions = {}): TokenBucket => {
  const config = {
    capacity: 10, // Maximum tokens
    refillRate: 1, // Tokens per second
    refillAmount: 1, // Tokens added per refill
    ...options,
  };
  
  let tokens = config.capacity;
  let lastRefill = Date.now();
  
  const refill = () => {
    const now = Date.now();
    const elapsed = (now - lastRefill) / 1000; // Convert to seconds
    const tokensToAdd = Math.floor(elapsed * config.refillRate) * config.refillAmount;
    
    if (tokensToAdd > 0) {
      tokens = Math.min(config.capacity, tokens + tokensToAdd);
      lastRefill = now;
    }
  };
  
  return {
    /**
     * Try to consume tokens
     * @param {number} count - Number of tokens to consume
     * @returns {boolean} True if tokens were consumed
     */
    consume: (count: number = 1): boolean => {
      refill();
      
      if (tokens >= count) {
        tokens -= count;
        return true;
      }
      
      return false;
    },
    
    /**
     * Check available tokens without consuming
     * @returns {number} Available tokens
     */
    available: (): number => {
      refill();
      return tokens;
    },
    
    /**
     * Get bucket status
     * @returns {Object} Bucket status
     */
    getStatus: (): TokenBucketStatus => {
      refill();
      return {
        tokens,
        capacity: config.capacity,
        refillRate: config.refillRate,
        lastRefill,
      };
    },
    
    /**
     * Reset bucket to full capacity
     */
    reset: (): void => {
      tokens = config.capacity;
      lastRefill = Date.now();
    },
  };
};

/**
 * Sliding window rate limiter
 * @param {SlidingWindowOptions} options - Sliding window configuration
 * @returns {SlidingWindow} Sliding window rate limiter
 */
export const slidingWindow = (options: SlidingWindowOptions = {}): SlidingWindow => {
  const config = {
    windowMs: 60000, // 1 minute window
    maxRequests: 100, // Max requests per window
    ...options,
  };
  
  const requests = new Map();
  
  return {
    /**
     * Check if request is allowed
     * @param {string} key - Request identifier
     * @returns {boolean} True if request is allowed
     */
    isAllowed: (key: string = 'default'): boolean => {
      const now = Date.now();
      const windowStart = now - config.windowMs;
      
      // Get request history for this key
      let history = requests.get(key) || [];
      
      // Remove old requests outside the window
      history = history.filter((timestamp: number) => timestamp > windowStart);
      
      // Check if limit would be exceeded
      if (history.length >= config.maxRequests) {
        // Update the history even though request is denied
        requests.set(key, history);
        return false;
      }
      
      // Add current request and update history
      history.push(now);
      requests.set(key, history);
      
      return true;
    },
    
    /**
     * Get current window status
     * @param {string} key - Request identifier
     * @returns {Object} Window status
     */
    getStatus: (key: string = 'default'): SlidingWindowStatus => {
      const now = Date.now();
      const windowStart = now - config.windowMs;
      const history = requests.get(key) || [];
      
      // Count recent requests
      const recentRequests = history.filter((timestamp: number) => timestamp > windowStart);
      
      return {
        requests: recentRequests.length,
        remaining: Math.max(0, config.maxRequests - recentRequests.length),
        windowStart,
        windowEnd: now,
        resetTime: recentRequests.length > 0 ? recentRequests[0] + config.windowMs : now,
      };
    },
    
    /**
     * Clear history for a key
     * @param {string} key - Key to clear
     */
    clear: (key: string = 'default'): void => {
      requests.delete(key);
    },
    
    /**
     * Clear all history
     */
    clearAll: (): void => {
      requests.clear();
    },
  };
};

export default {
  rateLimit,
  withRateLimit,
  withRateLimitAsync,
  tokenBucket,
  slidingWindow,
};
